import { ClassroomCardSkeleton } from "@/components/skeletons";
import { AppButton } from "@/components/teacher/AppButton";
import { StatPill } from "@/components/teacher/StatPill";
import { MyClassroomsDocument } from "@/generated/graphql";
import { format, useT } from "@/i18n";
import { useQuery } from "@apollo/client/react";
import { useNavigate } from "@tanstack/react-router";
import { HiArrowRight } from "react-icons/hi";

const SKELETON_KEYS = ["a", "b", "c", "d"];

interface ClassroomGridProps {
  /** Where a card leads. Omitted opens the classroom itself. */
  onSelect?: (classroomId: string) => void;
}

/** The teacher's classrooms as cards, each opening the classroom it names. */
export function ClassroomGrid({ onSelect }: ClassroomGridProps) {
  const navigate = useNavigate();
  const { data, loading, error, refetch } = useQuery(MyClassroomsDocument);
  const t = useT();

  const classrooms = data?.myClassrooms ?? [];

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        {SKELETON_KEYS.map((key) => (
          <ClassroomCardSkeleton key={key} />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="app-card space-y-3 p-6 text-center">
        <p className="body text-app-muted">{t["classrooms.loadError"]}</p>
        <AppButton tone="quiet" onClick={() => void refetch()}>
          {t["common.tryAgain"]}
        </AppButton>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
      {classrooms.map((classroom) => (
        <button
          key={classroom.id}
          type="button"
          onClick={() =>
            onSelect
              ? onSelect(classroom.id)
              : navigate({
                  to: "/teacher/classrooms/$classroomId",
                  params: { classroomId: classroom.id },
                })
          }
          className="app-card app-card-link group flex flex-col items-start gap-2 p-5 text-left"
        >
          <span className="flex w-full items-center gap-2">
            <span className="app-title flex-1 truncate">{classroom.name}</span>
            <HiArrowRight className="h-4 w-4 text-app-ink transition-transform group-hover:translate-x-1" />
          </span>
          <StatPill>
            {format(t["classrooms.studentCount"], { count: classroom.studentCount })}
          </StatPill>
        </button>
      ))}

      {classrooms.length === 0 && (
        <p className="app-card body-sm p-6 text-app-muted">{t["classrooms.empty"]}</p>
      )}
    </div>
  );
}
